import React, { useState } from 'react';
import { Download, AlertCircle, Check, ChevronDown } from 'lucide-react';
import { downloadReport } from '../lib/download';
import DesktopHint from './DesktopHint';

/**
 * ReportDownload — Excel / CSV buttons for a report endpoint.
 *
 * Built 26 Aug 2026. One component so every report downloads the same way and
 * reports the same errors. Excel gets the full workbook (every sheet); CSV is
 * one table, so `csvSheets` names which ones are offered.
 *
 * On native (Week 4 store build) the file goes to the OS share sheet instead
 * of a browser download — see lib/download.js — so the confirmation copy
 * says "ready to save" rather than "saved".
 */
export default function ReportDownload({
  url,
  params = {},
  fallbackName = 'marakin-report',
  csvSheets = [{ index: 0, label: 'CSV' }],
  note,
}) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [showOther, setShowOther] = useState(false);

  const run = async (key, extra) => {
    setBusy(key); setError(null); setDone(null);
    try {
      const result = await downloadReport(url, { ...params, ...extra }, fallbackName);
      setDone(result);
    } catch (err) {
      setError(err.message || 'Could not generate that report.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <DesktopHint>Easier on a computer — but the download works here too.</DesktopHint>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary btn-sm"
          disabled={!!busy}
          onClick={() => run('xlsx', { format: 'xlsx' })}
        >
          <Download size={14} /> {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
        </button>
        {/* B-48 / UC-8: Excel is the one default; CSVs sit under a fold. */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-expanded={showOther}
          onClick={() => setShowOther(v => !v)}
        >
          Other formats <ChevronDown size={14} style={{ transform: showOther ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
      </div>
      {showOther && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {csvSheets.map(s => (
          <button
            key={s.index}
            className="btn btn-ghost btn-sm"
            disabled={!!busy}
            onClick={() => run(`csv-${s.index}`, { format: 'csv', sheet: s.index })}
          >
            <Download size={14} /> {busy === `csv-${s.index}` ? 'Preparing…' : s.label}
          </button>
        ))}
      </div>
      )}

      {note && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
          {note}
        </p>
      )}
      {error && (
        <div className="form-error" style={{ marginTop: 8 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {done?.cancelled && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0 0' }}>
          Share sheet closed without saving — tap again when you're ready.
        </p>
      )}
      {done && !done.truncated && !done.cancelled && (
        <p style={{ fontSize: 12, color: '#2D7A4F', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={13} /> {done.shared ? `${done.name} is ready — pick where to save or send it` : `Saved ${done.name}`}
        </p>
      )}
      {done?.truncated && !done.cancelled && (
        <p style={{ fontSize: 12, color: '#8A6222', margin: '8px 0 0', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            {done.shared ? 'Prepared' : 'Saved'} {done.name} — but there was more than one file can hold, so the
            oldest rows were left out. Narrow the dates or pick one team to get
            the rest. The "About this report" tab has the numbers.
          </span>
        </p>
      )}
    </div>
  );
}
